import React, { ReactElement, useContext, useState } from 'react';
import styles from './dataProductDetailTab.module.scss';
import { useQuery } from 'react-query';
import {
  DataProduct,
  DataProductPurchase,
  IamACustomerDRMEntityType,
  IamACustomerDrmEntity,
} from 'v2/models';
import restFactory from 'v2/utils/restFactory';
import EditDataProductDescriptionModal from '../Modals/EditDescriptionModal/EditDescriptionModal';
import EditAccessModal from 'v2/common/DRM/DataProduct/Modals/EditAccessModal/EditAccessModal';
import EditCostsModal from 'v2/common/DRM/DataProduct/Modals/EditCostsModal/EditCostsModal';
import CurrentlyPurchasedField from 'v2/common/DRM/DataProduct/CurrentlyPurchasedField/CurrentlyPurchasedField';
import SectionHeader from 'v2/common/DRM/SectionHeader/SectionHeader';
import DataProductCosts from 'v2/common/DRM/DataProduct/DataProductCosts/DataProductCosts';
import DataProductPurchases from 'v2/common/DRM/DataProduct/DataProductPurchases/DataProductPurchases';
import EditPurchasesModal from '../Modals/EditPurchasesModal/EditPurchasesModal';
import { DataVendorContext } from 'v2/utils/dataVendorUtils';
import HtmlRenderer from 'v2/common/HtmlRenderer/HtmlRenderer';
import moment from 'moment';
import useGetStrapiUser from 'v2/hooks/useGetOwner';
import { Spinner } from 'react-bootstrap';
import classnames from 'classnames';
import DatasetOwnerField from './DatasetOwnerField/DatasetOwnerField';
import HangOnASecondModal, {
  HangOnASecondModalProps,
  useIamACustomerMessage,
} from '../../HangOnASecondModal/HangOnASecondModal';
import { DataProductQueryResult } from 'v2/pages/DataVendorDetails/DataProductsTab/DataProductsContainer';
import { useTranslation } from 'react-i18next';
import { TranslationKeys } from 'translations';
import { useHistory } from 'react-router-dom';

interface DataProductDetailTabProps {
  dataProduct: DataProduct;
}

interface updateCurrentlyACustomerParams {
  isCustomer: boolean;
  drmEntity: IamACustomerDrmEntity;
  id: number;
  onUpdate?: () => void;
}

interface NoDataContentProps {
  title: string;
  hasValue: boolean;
  children: ReactElement;
}

const drmPropertyMap = {
  [IamACustomerDrmEntity.DATASET]: 'currently_purchased',
  [IamACustomerDrmEntity.DATA_VENDOR]: 'am_a_customer',
};

export function NoDataContent({
  title,
  hasValue,
  children,
}: NoDataContentProps) {
  const { t } = useTranslation();

  if (!hasValue) {
    return (
      <label
        data-testid={`noData_${title.replaceAll(' ', '')}`}
        className={styles.noData}
      >
        Include {title} for this {t(TranslationKeys.dataProduct_one)}.
      </label>
    );
  }

  return children;
}

const DataProductDetailTab = ({ dataProduct }: DataProductDetailTabProps) => {
  const iamACustomerMessage = useIamACustomerMessage();
  const { refetch: refetchList, refetchDataVendor } =
    useContext(DataVendorContext);
  const [showEditDescriptionModal, setShowEditDescriptionModal] =
    useState(false);
  const [showEditAccessModal, setShowEditAccessModal] = useState(false);
  const [showEditCostsModal, setShowEditCostsModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [
    messageChangeOnCurrentlyPurchasedField,
    setMessageChangeOnCurrentlyPurchasedField,
  ] = useState<HangOnASecondModalProps | undefined>();

  const history = useHistory();
  const showDescription =
    !history.location.pathname.includes('data-set-details');

  const { data: dataProductDetails, refetch } = useQuery(
    ['Get specific data product by ID', dataProduct.id],
    async () =>
      await restFactory.get<DataProduct>(
        `/dj/api/v1/data_product/${dataProduct.id}`
      ),
    { cacheTime: 0 }
  );

  const { data: ownerData, isFetching: ownerIsFetching } = useGetStrapiUser(
    dataProductDetails?.owner
  );

  if (ownerIsFetching) {
    return (
      <div className={classnames(styles.loading)}>
        <Spinner animation="border" />
      </div>
    );
  }

  if (!dataProductDetails) {
    return <></>;
  }

  const handleUpdateDataProduct = () => {
    setShowEditDescriptionModal(false);
    setShowEditAccessModal(false);
    setShowEditCostsModal(false);
    refetch();
  };

  const handleCurrentlyPurchasedField = async (isACustomer: boolean) => {
    const updateCurrentlyACustomerParams: updateCurrentlyACustomerParams = {
      isCustomer: true,
      drmEntity: IamACustomerDrmEntity.DATA_VENDOR,
      id: dataProductDetails.data_vendor?.id as number,
      onUpdate: refetchDataVendor,
    };
    if (
      isACustomer &&
      dataProductDetails.data_vendor?.id !== null &&
      !dataProductDetails.data_vendor?.am_a_customer
    ) {
      setMessageChangeOnCurrentlyPurchasedField({
        onCancel: handleCloseCustomerModal,
        onOk: () => updateCurrentlyACustomer(updateCurrentlyACustomerParams),
        ...iamACustomerMessage.customerOfDatasetButNotDataVendorCustomer,
      });
    } else if (!isACustomer && dataProductDetails.data_vendor?.am_a_customer) {
      const dataProductsPurchased =
        await restFactory.get<DataProductQueryResult>(
          `/dj/api/v1/data_vendor/${dataProductDetails.data_vendor.id}/data_products/`,
          { currently_purchased: true }
        );
      if (dataProductsPurchased?.count === 0) {
        setMessageChangeOnCurrentlyPurchasedField({
          onCancel: handleCloseCustomerModal,
          onOk: () =>
            updateCurrentlyACustomer({
              ...updateCurrentlyACustomerParams,
              isCustomer: false,
            }),
          ...iamACustomerMessage.NotCustomerOfAnyDatasetButDataVendorCustomer,
        });
      }
    }
    refetchList?.();
    refetch();
  };

  const checkActivePurchases = (purchases: DataProductPurchase[]) => {
    const today = moment();
    return purchases.some((item) =>
      today.isBetween(item.purchase_date, item.renewal_date)
    );
  };

  const handleUpdateCurrentlyACustomerWhenPurchase = () => {
    updateCurrentlyACustomer({
      isCustomer: true,
      drmEntity: IamACustomerDrmEntity.DATASET,
      id: dataProductDetails.id,
    });
    if (
      dataProductDetails.data_vendor?.id !== null &&
      !dataProductDetails.data_vendor?.am_a_customer
    ) {
      updateCurrentlyACustomer({
        isCustomer: true,
        drmEntity: IamACustomerDrmEntity.DATA_VENDOR,
        id: dataProductDetails.data_vendor?.id as number,
        onUpdate: refetchDataVendor,
      });
    }
  };

  const handleUpdatePurchase = (dataProduct?: DataProduct) => {
    setShowPurchaseModal(false);
    if (dataProduct?.purchases) {
      const activePurchases = checkActivePurchases(dataProduct.purchases);
      if (!dataProductDetails.currently_purchased && activePurchases) {
        setMessageChangeOnCurrentlyPurchasedField({
          onCancel: handleCloseCustomerModal,
          onOk: handleUpdateCurrentlyACustomerWhenPurchase,
          ...iamACustomerMessage.withPurchasesButNotDatasetCustomer,
        });
      }
    }
    refetchList?.();
    refetch();
  };

  const updateCurrentlyACustomer = async ({
    drmEntity,
    id,
    isCustomer,
    onUpdate,
  }: updateCurrentlyACustomerParams) => {
    await restFactory.patch(`/dj/api/v1/${drmEntity}/${id}/`, {
      [drmPropertyMap[drmEntity]]: isCustomer,
    });
    setMessageChangeOnCurrentlyPurchasedField(undefined);
    onUpdate?.();
    refetch();
  };

  const handleCloseCustomerModal = () => {
    setMessageChangeOnCurrentlyPurchasedField(undefined);
  };

  const canEdit = dataProductDetails.is_editable;

  const dataProductEntity: IamACustomerDRMEntityType = {
    iAmACustomer: dataProductDetails.currently_purchased,
    id: dataProduct.id,
    type: IamACustomerDrmEntity.DATASET,
  };

  return (
    <div>
      <CurrentlyPurchasedField
        drmEntity={dataProductEntity}
        canEdit={canEdit}
        onChange={handleCurrentlyPurchasedField}
      />
      <DatasetOwnerField
        hasValue={!Number.isNaN(dataProductDetails.owner)}
        owner={ownerData}
        dataset={dataProductDetails}
      />
      {showDescription && (
        <div className={styles.field} data-cy="dataset-description-section">
          <SectionHeader
            title="Description"
            isEditable={canEdit}
            className="mb-3"
            onClick={() => setShowEditDescriptionModal(true)}
          />
          <NoDataContent
            title="the description"
            hasValue={!!dataProductDetails.description}
          >
            <div
              data-testid={`value_Description`}
              data-cy="dataset:description"
              className={styles.descriptionTexts}
            >
              <HtmlRenderer source={dataProductDetails.description} />
            </div>
          </NoDataContent>
        </div>
      )}
      <div className={styles.field} data-cy="dataset-access-details-section">
        <SectionHeader
          title="Access"
          isEditable={canEdit}
          className="mb-3"
          onClick={() => setShowEditAccessModal(true)}
        />
        <NoDataContent
          title="access details"
          hasValue={!!dataProductDetails.access_details}
        >
          <div data-testid={`value_Access`} className={styles.descriptionTexts}>
            <HtmlRenderer source={dataProductDetails.access_details} />
          </div>
        </NoDataContent>
      </div>
      <div className={styles.field} data-cy="dataset-costs-section">
        <SectionHeader
          title="Cost"
          isEditable={canEdit}
          className="mb-3"
          onClick={() => setShowEditCostsModal(true)}
        />
        <NoDataContent
          title="costs"
          hasValue={dataProductDetails.costs?.length > 0}
        >
          <DataProductCosts dataProduct={dataProductDetails} />
        </NoDataContent>
      </div>
      <div className={styles.field} data-cy="dataset-purchase-history-section">
        <SectionHeader
          title="Purchase History"
          isEditable={canEdit}
          className="mb-3"
          onClick={() => setShowPurchaseModal(true)}
        />
        <NoDataContent
          title="the purchase history"
          hasValue={dataProductDetails.purchases?.length > 0}
        >
          <DataProductPurchases dataProduct={dataProductDetails} />
        </NoDataContent>
      </div>
      {showDescription && (
        <EditDataProductDescriptionModal
          show={showEditDescriptionModal}
          dataProduct={dataProductDetails}
          onClose={handleUpdateDataProduct}
        />
      )}
      <EditAccessModal
        show={showEditAccessModal}
        dataProduct={dataProductDetails}
        onClose={handleUpdateDataProduct}
      />
      <EditCostsModal
        show={showEditCostsModal}
        dataProduct={dataProductDetails}
        onClose={handleUpdateDataProduct}
      />
      {showPurchaseModal && (
        <EditPurchasesModal
          dataProduct={dataProductDetails}
          onClose={handleUpdatePurchase}
        />
      )}
      {messageChangeOnCurrentlyPurchasedField && (
        <HangOnASecondModal {...messageChangeOnCurrentlyPurchasedField} />
      )}
    </div>
  );
};

export default DataProductDetailTab;
